import React, { useState, useRef, useEffect } from 'react';
import './InlinePickerSelect.css';

/**
 * InlinePickerSelect - Searchable dropdown with "Create new" option.
 * Matches the client picker UI pattern for consistency.
 *
 * Props:
 *   options       - [{ value, label }]
 *   value         - { value, label } or null
 *   onChange      - (selected) => void
 *   onCreateNew   - (name) => Promise (called when creating new item, should return the new option)
 *   placeholder   - string
 *   createLabel   - string (e.g. "Create new category")
 *   searchPlaceholder - string
 *   label         - string (field label above the picker)
 *   error         - string (error message)
 */
const InlinePickerSelect = ({
  options = [],
  value,
  onChange,
  onCreateNew,
  placeholder = 'Select...',
  createLabel = 'Create new',
  searchPlaceholder = 'Search...',
  label,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setCreating(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (opt) => {
    onChange(opt);
    setOpen(false);
    setSearch('');
    setCreating(false);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (onCreateNew) {
        await onCreateNew(name);
      }
      setCreating(false);
      setNewName('');
      setOpen(false);
    } catch { /* handled by parent */ }
    setIsSubmitting(false);
  };

  return (
    <div className="ips-wrap" ref={ref}>
      {label && <label className="ips-label">{label}</label>}
      <button
        type="button"
        className={`ips-trigger ${open ? 'ips-trigger--open' : ''} ${value ? 'ips-trigger--filled' : ''}`}
        onClick={() => { setOpen(!open); setCreating(false); setSearch(''); }}
      >
        <span>{value ? value.label : placeholder}</span>
        <svg className={`ips-chevron ${open ? 'ips-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="ips-dropdown">
          {/* Search */}
          <div className="ips-search">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8e8e93" strokeWidth="1.3">
              <circle cx="7" cy="7" r="5" /><path d="M11 11L14 14" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={creating ? '' : search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              disabled={creating}
            />
          </div>

          <div className="ips-list">
            {/* Create new - at top */}
            {onCreateNew && !creating && (
              <button type="button" className="ips-item ips-item--create" onClick={() => { setCreating(true); setSearch(''); }}>
                <div className="ips-item-icon ips-item-icon--add">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 2V12M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="ips-item-info">
                  <span className="ips-item-name">{createLabel}</span>
                </div>
              </button>
            )}

            {/* Inline create form */}
            {creating && (
              <div className="ips-create-form">
                <input
                  type="text"
                  className="ips-create-input"
                  placeholder="Enter name..."
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setCreating(false); }}
                  autoFocus
                />
                <button type="button" className="ips-create-cancel" onClick={() => { setCreating(false); setNewName(''); }}>
                  Cancel
                </button>
                <button type="button" className="ips-create-btn" onClick={handleCreate} disabled={!newName.trim() || isSubmitting}>
                  {isSubmitting ? '...' : 'Add'}
                </button>
              </div>
            )}

            {/* Options */}
            {!creating && filtered.map(opt => {
              const isActive = value?.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`ips-item ${isActive ? 'ips-item--active' : ''}`}
                  onClick={() => handleSelect(opt)}
                >
                  <span className="ips-item-name">{opt.label}</span>
                  {isActive && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7.5L5.5 11L12 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              );
            })}

            {!creating && filtered.length === 0 && (
              <div className="ips-empty">No results found</div>
            )}
          </div>
        </div>
      )}

      {error && <span className="cm_error_msg">{error}</span>}
    </div>
  );
};

export default InlinePickerSelect;
